import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, dateShort, timeAgo, productImage } from "@/lib/api";
import { SectionTitle, Field, Thumb, ProcurementBadge, SelectionBadge } from "@/components/Bits";
import FilesPanel from "@/components/FilesPanel";
import NotesPanel from "@/components/NotesPanel";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";
const STATUSES = ["Not Ready", "Ready to Order", "Ordered", "Acknowledged", "Backordered", "In Production",
  "Ready to Ship", "Shipped", "Partially Delivered", "Delivered", "Damaged", "Return Requested",
  "Returned", "Installed", "Cancelled"];

const FIELDS = [
  ["po_number", "PO Number", "text"],
  ["vendor_order_number", "Vendor Order Number", "text"],
  ["order_date", "Order Date", "date"],
  ["est_ship_date", "Estimated Ship Date", "date"],
  ["actual_ship_date", "Actual Ship Date", "date"],
  ["est_delivery_date", "Estimated Delivery Date", "date"],
  ["actual_delivery_date", "Actual Delivery Date", "date"],
  ["carrier", "Carrier", "text"],
  ["tracking_number", "Tracking Number", "text"],
  ["tracking_url", "Tracking URL", "text"],
  ["receiving_location", "Receiving Location", "text"],
  ["condition_at_delivery", "Condition at Delivery", "text"],
  ["installation_date", "Installation Date", "date"],
];

export default function ProcurementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/procurement/${id}`);
      setItem(data);
      setForm(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!item) return <div className="py-24 text-center text-sm text-fog">Loading item…</div>;
  const p = item.product || {};

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { status: form.status, assigned_to: form.assigned_to || null };
    FIELDS.forEach(([k]) => (body[k] = form[k] || null));
    try {
      await api.patch(`/procurement/${id}`, body);
      toast.success("Procurement saved");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-10 pb-10" data-testid="procurement-detail-page">
      <button onClick={() => navigate("/procurement")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Procurement
      </button>

      <div className="flex flex-col gap-5 border-b border-line pb-8 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="overline mb-3">{p.category} · {p.project_name}</div>
          <h1 data-testid="procurement-detail-title" className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">{p.name}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <ProcurementBadge status={item.status} testId="procurement-detail-status" />
            <SelectionBadge status={p.selection_status} />
            {p.space_name && <span className="text-sm text-stone">{p.space_name}</span>}
          </div>
        </div>
        <button data-testid="procurement-save-btn" onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60">
          <Save className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
        <div className="space-y-8">
          <div className="card-surface overflow-hidden">
            {productImage(p) ? (
              <img src={productImage(p)} alt={p.name} className="aspect-square w-full object-cover" />
            ) : (
              <Thumb className="aspect-square w-full" rounded="rounded-none" />
            )}
            <div className="space-y-3 p-6 text-sm">
              <Link to={`/products/${p.id}`} className="block text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Open product record →</Link>
              {[["SKU", p.sku], ["Brand", p.brand], ["Dimensions", p.dimensions], ["Finish", p.color_finish], ["Lead Time", p.lead_time], ["Quantity", p.quantity]].map(([l, v]) => (
                <div key={l} className="flex justify-between gap-4 border-b border-line pb-2 last:border-0">
                  <span className="text-stone">{l}</span>
                  <span className="num text-right text-ink">{v || "—"}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card-surface p-6">
            <SectionTitle>Vendor</SectionTitle>
            {item.vendor ? (
              <div className="space-y-2 text-sm">
                <Link to={`/vendors/${item.vendor.id}`} className="font-serif text-lg text-ink hover:text-lime-dark">{item.vendor.name}</Link>
                <div className="text-stone">{item.vendor.account_rep}</div>
                <div className="text-stone">{item.vendor.email}</div>
                <div className="num text-stone">{item.vendor.phone}</div>
                <div className="text-xs text-fog">Trade acct {item.vendor.trade_account_number}</div>
              </div>
            ) : (
              <p className="text-sm text-fog">No vendor assigned.</p>
            )}
          </div>

          <div className="card-surface p-6">
            <SectionTitle>Financials</SectionTitle>
            <div className="space-y-3 text-sm">
              {[["Client Unit Price", p.client_unit_price], ["Client Total", p.client_total], ["Designer Total Cost", p.designer_total_cost], ["Estimated Profit", p.estimated_profit]].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-line pb-2 last:border-0">
                  <span className="text-stone">{l}</span>
                  <span className="num text-ink">{money(v)}</span>
                </div>
              ))}
              <div className="flex justify-between">
                <span className="text-stone">Margin</span>
                <span className="num text-ink">{(p.margin_percentage || 0).toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-10">
          <form onSubmit={save} className="card-surface p-7" data-testid="procurement-form">
            <SectionTitle>Order &amp; Shipping</SectionTitle>
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Procurement Status">
                <select data-testid="procurement-status-select" value={form.status || ""} onChange={(e) => setForm({ ...form, status: e.target.value })} className={input}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Assigned To">
                <select data-testid="procurement-assigned-select" value={form.assigned_to || ""} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className={input}>
                  <option value="">— Unassigned —</option>
                  {users.filter((u) => u.role !== "client").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
              {FIELDS.map(([k, label, type]) => (
                <Field key={k} label={label}>
                  <input
                    data-testid={`procurement-${k.replace(/_/g, "-")}-input`}
                    type={type}
                    value={type === "date" ? (form[k] || "").slice(0, 10) : form[k] || ""}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className={type === "date" ? `num ${input}` : input}
                  />
                </Field>
              ))}
            </div>
          </form>

          <div className="card-surface p-7">
            <NotesPanel entityType="procurement" entityId={id} projectId={item.project_id} />
          </div>

          <div className="card-surface p-7">
            <SectionTitle>Files</SectionTitle>
            <FilesPanel projectId={item.project_id} procurementId={id} productId={p.id} compact />
          </div>

          <div className="card-surface p-7">
            <SectionTitle>Activity</SectionTitle>
            <div className="divide-y divide-line">
              {(item.activities || []).map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4 py-3 text-sm">
                  <span className="text-ink"><span className="font-medium">{a.user_name}</span> {a.action}</span>
                  <span className="num text-xs text-fog">{timeAgo(a.created_at)}</span>
                </div>
              ))}
              {!item.activities?.length && <p className="py-3 text-sm text-fog">No activity yet.</p>}
            </div>
          </div>
        </div>
      </div>
      <div className="text-xs text-fog">Created {dateShort(item.created_at)}</div>
    </div>
  );
}
